/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface MaintenanceFacilityProps {
  id: string;
  name: string;
  abbreviation: string;
  locationId: string | null;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class MaintenanceFacilityRecord extends Record<MaintenanceFacilityProps>({
  id: null as any,
  name: null as any,
  abbreviation: null as any,
  locationId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    abbreviation: string;
    locationId: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): MaintenanceFacilityRecord {
    if (json == null) {
      throw new Error('MaintenanceFacilityRecord.fromJS: parameter cannot be null');
    }
    return new MaintenanceFacilityRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'MaintenanceFacilityJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'MaintenanceFacilityJson.name'),
      abbreviation: getFromOrThrow(
        json,
        (j) => j.abbreviation,
        'MaintenanceFacilityJson.abbreviation',
      ),
      locationId: json.locationId,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    abbreviation: string;
    locationId: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [MaintenanceFacilityRecord, Error[]] {
    if (json == null) {
      throw new Error('MaintenanceFacilityRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new MaintenanceFacilityRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'MaintenanceFacilityJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'MaintenanceFacilityJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      abbreviation: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.abbreviation, 'MaintenanceFacilityJson.abbreviation'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      locationId: tryOrDefault(
        () => json.locationId,
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: MaintenanceFacilityProps) {
    super(props);
    if (props == null) {
      throw new Error('MaintenanceFacilityRecord constructor parameter cannot be null');
    }
  }
}

export abstract class MaintenanceFacilityViewModelBase implements MaintenanceFacilityProps {
  protected readonly _props: MaintenanceFacilityProps;
  private _json?: MaintenanceFacilityProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get abbreviation(): string {
    return this._props.abbreviation;
  }
  get locationId(): string | null {
    return this._props.locationId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: MaintenanceFacilityProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        abbreviation: this._props.abbreviation,
        locationId: this._props.locationId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllMaintenanceFacilityEntities = (state: AppState) =>
  state.entityStores.maintenanceFacility.entities;

export const selectMaintenanceFacilityById = (id: string) => (state: AppState) =>
  state.entityStores.maintenanceFacility.entities.get(id);

export const selectMaintenanceFacilityMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.maintenanceFacility.meta.get(id);

export const selectMaintenanceFacilityAndMetadataById = (id: string) =>
  createSelector(
    selectMaintenanceFacilityById(id),
    selectMaintenanceFacilityMetadataById(id),
    (maintenanceFacility, meta) => ({ maintenanceFacility, meta }),
  );

export function selectMaintenanceFacilityEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllMaintenanceFacilityEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, MaintenanceFacilityRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectMaintenanceFacilityEntitiesByName(name: string) {
  return createSelector(selectAllMaintenanceFacilityEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectMaintenanceFacilityEntitiesByAbbreviation(abbreviation: string) {
  return createSelector(selectAllMaintenanceFacilityEntities, (entities) =>
    entities.filter((e) => e.abbreviation === abbreviation).valueSeq(),
  );
}

export function selectMaintenanceFacilityEntitiesByLocationId(locationId: string) {
  return createSelector(selectAllMaintenanceFacilityEntities, (entities) =>
    entities.filter((e) => e.locationId === locationId).valueSeq(),
  );
}
//#endregion Selectors
